import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GitCompare, X, Sparkles } from "lucide-react";
import { compareStore } from "@/lib/compareStore";
import { formatPrice } from "@/data/properties";

// Floating tray for selected properties -> "Compare with Abay AI".
export const CompareTray = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(compareStore.get());
    return compareStore.subscribe(setItems);
  }, []);

  const compareNow = () => {
    const list = compareStore.get();
    if (list.length < 2) return;
    const summary = list
      .map((p, i) => `${i + 1}. ${p.title} — ${formatPrice(Number(p.price), p.currency)}${p.deal === "rent" ? "/mo" : ""}, ${p.deal}, ${p.bedrooms || 0} bed, ${p.bathrooms || 0} bath, ${p.square_meters || "?"} m², ${p.address || ""}`)
      .join("\n");
    const message = `Compare these ${list.length} Ethiopian properties for a buyer/investor. Provide a Markdown comparison table (price, size, beds/baths, location) followed by Pros/Cons and a clear recommendation of the best value:\n\n${summary}`;
    window.dispatchEvent(new CustomEvent("abay:ask", { detail: { message } }));
  };

  return (
    <AnimatePresence>
      {items.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          data-testid="compare-tray"
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 z-[55] w-[92vw] max-w-2xl -translate-x-1/2 rounded-2xl border border-border bg-white/95 p-2.5 shadow-[var(--shadow-lg)] backdrop-blur-md lg:bottom-4"
        >
          <div className="flex items-center gap-3">
            <span className="hidden shrink-0 items-center gap-1.5 pl-1 text-sm font-semibold text-[#1E3A8A] sm:flex">
              <GitCompare className="h-4 w-4" /> Compare ({items.length})
            </span>
            <div className="flex flex-1 items-center gap-2 overflow-x-auto thin-scroll">
              {items.map((p) => (
                <div key={p.id} data-testid="compare-tray-item" className="flex shrink-0 items-center gap-2 rounded-xl border border-border bg-white py-1 pl-1 pr-2">
                  <img src={p.img} alt={p.title || "Property thumbnail"} loading="lazy" decoding="async" className="h-8 w-8 rounded-lg object-cover" />
                  <span className="max-w-[110px] truncate text-xs font-medium text-slate-700">{p.title}</span>
                  <button onClick={() => compareStore.remove(p.id)} data-testid="compare-tray-remove" aria-label="Remove" className="text-slate-400 hover:text-[#DC2626]"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <button onClick={() => compareStore.clear()} data-testid="compare-tray-clear" className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100">Clear</button>
            <button
              onClick={compareNow}
              disabled={items.length < 2}
              data-testid="compare-tray-compare"
              className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#1E3A8A] px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#172D6B] disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">Compare with</span> Abay AI
            </button>
          </div>
          {items.length < 2 && <p className="pl-1 pt-1 text-[11px] text-slate-400">Add at least 2 properties to compare.</p>}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompareTray;
